import { apiRequest, apiRequestStream } from '../utils/axios-config';
import {
  chunkArray,
  CORRECTION_BATCH_THRESHOLD,
  CORRECTION_MAX_TOKENS_CHUNK,
  CORRECTION_MAX_TOKENS_SINGLE,
  getSplittableArray,
  mergeCorrectedChunks,
  parseLLMJsonResponse,
} from '../utils/exerciseLLMCorrection';

// ============= STATISTIQUES GÉNÉRALES =============
/**
 * `segment` (toutes les statistiques) : 'familles' (B2C, défaut), 'etablissements' (B2B), 'tout'.
 */
export const getDashboardStats = async (segment = 'familles') => {
  return await apiRequest('get', `/admin/stats`, { params: { segment } });
};

/**
 * Entonnoir d'onboarding, dérivé des FAITS MÉTIER (inscription, compagnon créé,
 * combats d'onboarding terminés, onboarding achevé) — plus des événements clients,
 * qui n'ont jamais rien émis. Rend un OBJET (et non plus un tableau) :
 * `{ period, instrumentedSince, steps[], trend[] }`.
 */
export const getOnboardingFunnel = async (days = 30, segment = 'familles') => {
  return await apiRequest('get', `/admin/stats/onboarding-funnel`, { params: { days, segment } });
};

/**
 * Jalons des comptes (premier combat d'onboarding, onboarding terminé, identifié,
 * payant) : `{ fenetreJours, retention, jalons[], payantsActifs, tendance[] }`.
 */
export const getAccountMilestones = async (weeks = 26, segment = 'familles') => {
  return await apiRequest('get', `/admin/stats/jalons-comptes`, { params: { weeks, segment } });
};

/**
 * Acquisition par source du compte (convention UTM v2) : `{ jours, etapes[],
 * lignes: [{ source, medium, campagne, etape, nombre }] }` — `source` null = non mesurée.
 */
export const getAcquisitionBySource = async (days = 30, segment = 'familles') => {
  return await apiRequest('get', `/admin/stats/acquisition`, { params: { days, segment } });
};

/** DAU / WAU / MAU par jour : `{ jours, serie: [{ jour, dau, wau, mau }] }`. */
export const getActivePlayers = async (days = 90, segment = 'familles') => {
  return await apiRequest('get', `/admin/stats/joueurs-actifs`, { params: { days, segment } });
};

/**
 * Clients payants : `{ jours, serie[{ jour, stripe, apple, google, abonnements, achats, abonnes,
 * passes, payants, mrr }], aujourdhui, derniers[] }` (`services/clientsPayants.service.js`).
 */
export const getPaidCustomers = async (days = 90, segment = 'familles') =>
  apiRequest('get', '/admin/stats/clients-payants', { params: { days, segment } });

export const getNewFamilies = async (days = 90, segment = 'familles') => {
  return await apiRequest('get', `/admin/stats/nouvelles-familles`, { params: { days, segment } });
};

/**
 * Sessions de jeu reconstituées depuis les parties :
 * `{ jours, pauseMinutes, partieMaxMinutes, synthese, distribution[], semaines[] }`.
 */
export const getPlaySessions = async (days = 90, pause = 30, segment = 'familles') => {
  return await apiRequest('get', `/admin/stats/sessions`, { params: { days, pause, segment } });
};

/**
 * Paliers d'aventure (6ᵉ, 12ᵉ, 16ᵉ niveau jouable terminé). `days` = comptes créés
 * dans les N derniers jours, 0 = tous : `{ comptes, aventuriers, paliers[], courbe[] }`.
 */
export const getAdventureMilestones = async (days = 0, segment = 'familles') => {
  return await apiRequest('get', `/admin/stats/aventure-paliers`, { params: { days, segment } });
};

/**
 * Évolution jour par jour d'un niveau d'aventure (débloqués, terminés, parties,
 * échecs) sur `fenetre` jours ; `days` et `segment` filtrent les comptes comme
 * `getAdventureMilestones` : `{ niveauId, fenetre, jours[] }`.
 */
export const getAdventureLevelDaily = async (niveauId, { days = 0, segment = 'familles', fenetre = 30 } = {}) => {
  return await apiRequest('get', `/admin/stats/aventure-niveaux/${encodeURIComponent(niveauId)}/jours`, {
    params: { days, segment, fenetre },
  });
};

// Trous de disponibilité du service : où le pool validé est mince (dégradations/échecs).
export const getServingGaps = async ({ hours = 168, limit = 200 } = {}) => {
  return await apiRequest('get', `/admin/serving-gaps`, { params: { hours, limit } });
};

// Notions demandées par les leçons scannées, absentes de l'arbre (agrégé, migration 211).
export const getNotionsDemandees = async ({ tri = 'scans', inclureAjoutees = false } = {}) => {
  return await apiRequest('get', `/admin/notions-demandees`, {
    params: { tri, inclureAjoutees: inclureAjoutees ? 1 : 0 },
  });
};

export const marquerNotionDemandeeAjoutee = async (id, ajoutee = true) => {
  return await apiRequest('post', `/admin/notions-demandees/${id}/ajoutee`, { ajoutee });
};

// Pages enseignants : audience des pages, usage des générateurs, dernière publication SEO.
export const getTeacherPages = async (days = 30) => {
  return await apiRequest('get', `/admin/teacher-pages`, { params: { days } });
};

/** Harnais notion observée : même code que /dictation/correct et /wordlists/correct. */
export const probeErrorClassification = async (payload) => {
  return await apiRequest('post', '/admin/error-classification/probe', payload, { timeout: 90000 });
};

export const listErrorClassificationCache = async (params = {}) => {
  return await apiRequest('get', '/admin/error-classification/cache', { params });
};

export const getErrorClassificationHeuristic = async () => {
  return await apiRequest('get', '/admin/error-classification/heuristic');
};

export const classifyErrorHeuristic = async ({ expected, produced } = {}) => {
  return await apiRequest('get', '/admin/error-classification/heuristic/classify', {
    params: { expected, produced },
  });
};

// ============= CATALOGUE ITEMS (cosmetic_items) =============
export const getCatalogItems = async (params = {}) => {
  return await apiRequest('get', '/admin/catalog/items', { params });
};

export const getCatalogItem = async (id) => {
  return await apiRequest('get', `/admin/catalog/items/${id}`);
};

export const updateCatalogItem = async (id, data) => {
  return await apiRequest('patch', `/admin/catalog/items/${id}`, data);
};

/** Cadres d'inventaire publiés par le serveur (`scripts/assets/publier-cadres-icone.js`). */
export const getCatalogIconFrames = async () => {
  return await apiRequest('get', '/admin/catalog/cadres-icone');
};

export const createCatalogItem = async (data) => {
  return await apiRequest('post', '/admin/catalog/items', data);
};

/**
 * Dessin d'un objet porté par le compagnon. `formData` : `dessin` (png/webp) et,
 * pour un objet qui entoure le compagnon, `masque` — les pixels peints du masque
 * marquent ce qui passe DERRIÈRE lui, et le serveur en tire les deux moitiés dans
 * le même cadre. L'icône d'inventaire est fabriquée au passage.
 */
export const uploadCatalogItemDrawing = async (id, formData) => {
  return await apiRequest('post', `/admin/catalog/items/${id}/dessin`, formData, {
    transformRequest: [(data, headers) => {
      delete headers['Content-Type'];
      return data;
    }],
  });
};

export const simulateCatalogEffects = async ({ effects, sampleGain } = {}) => {
  return await apiRequest('post', '/admin/catalog/simulate', { effects, sampleGain });
};

export const getMarketingAnalytics = async (days = 30) => {
  return await apiRequest('get', `/admin/analytics`, { params: { days } });
};

// ============= GESTION DES EXERCICES =============
export const getExercises = async (filters = {}) => {
  return await apiRequest('get', `/admin/exercises`, { params: filters });
};

export const getExerciseTaxonomyStats = async () => {
  return await apiRequest('get', `/admin/exercises/taxonomy-stats`);
};

/** Lots de génération présents en base, pour le filtre « Source » de la liste. */
export const getExerciseSources = async () => {
  return await apiRequest('get', `/admin/exercises/sources`);
};

export const reassignExerciseTaxonomy = async ({ source, target }) => {
  return await apiRequest('post', `/admin/exercises/taxonomy-stats/reassign`, { source, target });
};

export const getExerciseById = async (id) => {
  return await apiRequest('get', `/admin/exercises/${id}`);
};

export const createExercise = async (exerciseData) => {
  return await apiRequest('post', `/admin/exercises`, exerciseData);
};

export const updateExercise = async (id, exerciseData) => {
  return await apiRequest('put', `/admin/exercises/${id}`, exerciseData);
};

export const deleteExercise = async (id) => {
  return await apiRequest('delete', `/admin/exercises/${id}`);
};

export const toggleExerciseStatus = async (id) => {
  return await apiRequest('patch', `/admin/exercises/${id}/toggle`);
};

export const generateExercise = async (generationData) => {
  return await apiRequest('post', `/admin/exercises/generate`, generationData);
};

/** Usage réel d'un exercice (journal des réponses) : `{ ...stats, recentUsage }`. */
export const getExerciseUsageStats = async (id) => {
  const result = await getExerciseById(id);
  return { ...(result.stats || {}), recentUsage: result.recentUsage || [] };
};

/**
 * Précédent, suivant, rang et total de l'exercice dans la liste filtrée et triée
 * comme l'écran de liste (`params` = `parametresApiListe(filtres)`).
 */
export const getExerciseNeighbours = async (id, params = {}) => {
  return await apiRequest('get', `/admin/exercises/${id}/voisins`, { params });
};

// ============= GESTION DES PROMPTS PERSONNALISÉS =============
export const getPrompts = async (filters = {}) => {
  return await apiRequest('get', `/admin/prompts`, { params: filters });
};

export const getActivePromptsByGameType = async (gameType) => {
  return await apiRequest('get', `/admin/prompts/active/${gameType}`);
};

export const getPromptById = async (id) => {
  return await apiRequest('get', `/admin/prompts/${id}`);
};

export const createPrompt = async (promptData) => {
  return await apiRequest('post', `/admin/prompts`, promptData);
};

export const updatePrompt = async (id, promptData) => {
  return await apiRequest('put', `/admin/prompts/${id}`, promptData);
};

export const deletePrompt = async (id) => {
  return await apiRequest('delete', `/admin/prompts/${id}`);
};

export const logPromptUsage = async (id, usageData) => {
  return await apiRequest('post', `/admin/prompts/${id}/log-usage`, usageData);
};

// Récupérer les instructions de format pour un type de jeu
export const getFormatInstructions = async (gameType) => {
  return await apiRequest('get', `/admin/prompts/format-instructions/${gameType}`);
};

// ✨ NOUVELLES FONCTIONS POUR L'OPTIMISATION DES PROMPTS

// Évaluer un exercice généré
export const evaluateExercise = async (evaluationData) => {
  return await apiRequest('post', '/admin/prompts/evaluate', evaluationData);
};

// Adapter un exercice basé sur l'évaluation
export const adaptExercise = async (adaptationData) => {
  return await apiRequest('post', '/admin/prompts/adapt', adaptationData);
};

// Optimiser un prompt basé sur les évaluations
export const optimizePrompt = async (optimizationData) => {
  return await apiRequest('post', '/admin/prompts/optimize', optimizationData);
};

// ============= ✨ NOUVELLES MÉTHODES POUR PREVIEW ET TEST DE PROMPTS =============

// Prévisualiser le prompt qui sera utilisé pour des paramètres donnés
export const previewPrompt = async (previewData) => {
  return await apiRequest('post', `/admin/exercises/preview-prompt`, previewData);
};

// Tester un prompt et voir les résultats générés
export const testPrompt = async (testData) => {
  return await apiRequest('post', `/admin/exercises/test-prompt`, testData);
};

// Sauvegarder une version de prompt avec historique
export const savePromptVersion = async (promptData) => {
  return await apiRequest('post', `/admin/prompts/save-version`, promptData);
};

// Comparer plusieurs versions de prompts
export const comparePrompts = async (comparisonData) => {
  return await apiRequest('post', `/admin/exercises/compare-prompts`, comparisonData);
};

// Obtenir l'historique des versions d'un prompt
export const getPromptHistory = async (promptId) => {
  return await apiRequest('get', `/admin/prompts/${promptId}/history`);
};

// Analyser la qualité d'un prompt (métriques)
export const analyzePromptQuality = async (analysisData) => {
  return await apiRequest('post', `/admin/prompts/analyze-quality`, analysisData);
};

// ============= ✨ CHAT AVEC LLM =============

// Discuter avec le LLM
export const chatWithLLM = async (chatData) => {
  return await apiRequest('post', `/admin/chat-llm`, chatData);
};

export function getSplittableArrayKey(gameType) {
  return SPLITTABLE_ARRAY_KEYS[gameType] || null;
}

async function requestExerciseCorrectionLLM({ gameType, content, parameters, chunkInfo, maxTokens, singleItem = false }) {
  // Le prompt est construit côté backend (mêmes règles que la correction en masse)
  // et l'appel passe par le client LLM résilient (retry + fallback Scaleway).
  return apiRequest('post', `/admin/exercises/correct-llm`, {
    gameType,
    content,
    parameters,
    chunkInfo,
    singleItem,
    maxTokens,
  });
}

// Corriger/améliorer un exercice existant via LLM
export const correctExerciseWithLLM = async ({ gameType, content, parameters }) => {
  const splittable = getSplittableArray(gameType, content);

  if (splittable && splittable.items.length > CORRECTION_BATCH_THRESHOLD) {
    const chunks = chunkArray(splittable.items);
    const chunkResults = await Promise.all(
      chunks.map(async (chunkItems, index) => {
        const chunkContent = { ...content, [splittable.key]: chunkItems };
        const chunkInfo = {
          from: chunks.slice(0, index).reduce((sum, chunk) => sum + chunk.length, 0) + 1,
          to: chunks.slice(0, index + 1).reduce((sum, chunk) => sum + chunk.length, 0),
          total: splittable.items.length,
          chunkIndex: index + 1,
          chunkCount: chunks.length,
        };

        try {
          const response = await requestExerciseCorrectionLLM({
            gameType,
            content: chunkContent,
            parameters,
            chunkInfo,
            maxTokens: CORRECTION_MAX_TOKENS_CHUNK,
          });
          return {
            content: parseLLMJsonResponse(response.response || ''),
            issues: response.issues || [],
            semanticProposed: Boolean(response.semanticProposed),
          };
        } catch (error) {
          console.error(`Correction LLM — lot ${index + 1}/${chunks.length} échoué`, error);
          return { content: chunkContent, issues: [] };
        }
      }),
    );

    const merged = mergeCorrectedChunks(content, splittable.key, chunkResults.map((r) => r.content));
    return {
      response: JSON.stringify(merged, null, 2),
      batched: true,
      chunkCount: chunks.length,
      issues: chunkResults.flatMap((r) => r.issues),
      semanticProposed: chunkResults.some((r) => r.semanticProposed),
    };
  }

  return requestExerciseCorrectionLLM({
    gameType,
    content,
    parameters,
    maxTokens: CORRECTION_MAX_TOKENS_SINGLE,
  });
};

const CORRECTABLE_ITEM_ARRAY_KEYS = new Set(['words', 'blanks', 'pairs', 'items', 'sortItems', 'errors', 'questions']);

// Corriger un seul élément d'un exercice multi-éléments via LLM
export const correctExerciseItemWithLLM = async ({ gameType, content, parameters, arrayKey, itemIndex }) => {
  if (!CORRECTABLE_ITEM_ARRAY_KEYS.has(arrayKey)) {
    throw new Error(`Correction unitaire non supportée pour « ${arrayKey} »`);
  }

  const items = content?.[arrayKey];
  if (!Array.isArray(items) || itemIndex < 0 || itemIndex >= items.length) {
    throw new Error('Élément introuvable');
  }

  const singleContent = { ...content, [arrayKey]: [items[itemIndex]] };
  const chunkInfo = {
    from: itemIndex + 1,
    to: itemIndex + 1,
    total: items.length,
    chunkIndex: 1,
    chunkCount: 1,
  };

  const response = await requestExerciseCorrectionLLM({
    gameType,
    content: singleContent,
    parameters,
    chunkInfo,
    singleItem: true,
    maxTokens: 2000,
  });

  const parsed = parseLLMJsonResponse(response.response || '');
  const correctedItems = parsed?.[arrayKey];
  if (!Array.isArray(correctedItems) || correctedItems.length === 0) {
    throw new Error('Réponse LLM sans élément corrigé');
  }

  return correctedItems[0];
};

// Obtenir des suggestions de prompts via LLM
export const getSuggestedPrompts = async (suggestionData) => {
  return await apiRequest('post', `/admin/chat-llm/suggest-prompts`, suggestionData);
};

// ============= CORRECTION EN MASSE PAR LLM =============

// Lance la correction en masse via SSE — retourne la Response brute fetch pour streaming
export const startBulkCorrection = async ({ filters = {}, maxItems = 50, autoApply = false }) => {
  return await apiRequestStream('post', `/admin/exercises/bulk-correct`, {
    filters,
    maxItems,
    autoApply,
  });
};

// Applique une liste de corrections sélectionnées (garde-fou par défaut ; manualValidate pour relecture explicite)
export const applyBulkCorrections = async (corrections, { manualValidate = false } = {}) => {
  return await apiRequest('post', `/admin/exercises/bulk-correct/apply`, { corrections, manualValidate });
};

// Finalise un lot : valide les OK, applique/signale les exercices avec problèmes
export const finalizeBulkSafeguard = async (items) => {
  return await apiRequest('post', `/admin/exercises/bulk-correct/finalize`, { items });
};

// Marque des exercices comme validés (relecture OK, contenu inchangé)
export const validateBulkExercises = async (exerciseIds) => {
  return await apiRequest('post', `/admin/exercises/bulk-validate`, { exerciseIds });
};

// ============= GESTION DES UTILISATEURS =============
export const getUsers = async (filters = {}) => {
  return await apiRequest('get', `/admin/users`, { params: filters });
};

export const getUsersEvolution = async (granularity = 'month') => {
  return await apiRequest('get', `/admin/users/evolution?granularity=${granularity}`);
};

export const getUserById = async (id) => {
  return await apiRequest('get', `/admin/users/${id}`);
};

export const toggleUserStatus = async (id) => {
  return await apiRequest('post', `/admin/users/${id}/toggle`);
};

export const toggleUserAdminStatus = async (id) => {
  return await apiRequest('post', `/admin/users/${id}/toggle-admin`);
};

export const setUserAdminRole = async (id, role) => {
  return await apiRequest('post', `/admin/users/${id}/admin-role`, { role });
};

export const getAdminSession = async () => {
  return await apiRequest('get', `/admin/me`);
};

// Endpoint canonique pour les stats de combats
export const getUserCombatStats = async (id) => {
  return await apiRequest('get', `/admin/users/${id}/combat-stats`);
};

// Alias rétrocompatible vers le même endpoint backend
export const getUserGameStats = async (id) => {
  return await getUserCombatStats(id);
};

export const getUserChildren = async (id) => {
  return await apiRequest('get', `/admin/users/${id}/children`);
};

export const getUserParent = async (id) => {
  return await apiRequest('get', `/admin/users/${id}/parent`);
};

export const getUserCompetences = async (id) => {
  return await apiRequest('get', `/admin/users/${id}/competences`);
};

export const getUserLevelEstimate = async (id) => {
  return await apiRequest('get', `/admin/users/${id}/level-estimate`);
};

// ============= CRÉDITS D'USAGE (P5 — plancher scan / Sage) =============

/** Soldes scan + Sage d'un joueur. */
export const getUserUsageCredits = async (id) => {
  return await apiRequest('get', `/admin/users/${id}/usage-credits`);
};

/**
 * Octroi manuel d'un crédit (D-11). Le motif est OBLIGATOIRE et sans défaut :
 * un crédit rendu sans raison inscrite est un crédit dont on ne saura jamais
 * s'il a converti (CONCEPT_MODELE_QUOTAS §6.2).
 * @param {string} id
 * @param {{ limitType: 'lesson_scan'|'sage_session', reason: string, note?: string, amount?: number }} payload
 */
export const grantUserUsageCredit = async (id, payload) => {
  return await apiRequest('post', `/admin/users/${id}/usage-credits/grant`, payload);
};

// ============= ABONNEMENT OFFERT =============

/**
 * Offre Aventurier (premium) ou Champion (premium_plus) pour N jours à partir
 * d'aujourd'hui. Refusé (409) si le compte a un abonnement payant en cours.
 * @param {string} id
 * @param {{ planType: 'premium'|'premium_plus', durationDays: number, reason: string, note?: string }} payload
 */
export const grantUserSubscription = async (id, payload) => {
  return await apiRequest('post', `/admin/users/${id}/subscription/grant`, payload);
};

/** Journal des abonnements offerts à un compte. */
export const getUserSubscriptionGrants = async (id) => {
  return await apiRequest('get', `/admin/users/${id}/subscription/grants`);
};

// ============= HISTORIQUE DE NAVIGATION =============
export const getUserNavigationHistory = async (userId, page = 1, limit = 50) => {
  return await apiRequest('get', `/admin/users/${userId}/history`, { 
    params: { page, limit } 
  });
};

export const getUserSessions = async (userId, page = 1, limit = 20) => {
  return await apiRequest('get', `/admin/users/${userId}/sessions`, { 
    params: { page, limit } 
  });
};

export const getUserSessionDetails = async (userId, sessionId) => {
  return await apiRequest('get', `/admin/users/${userId}/sessions/${sessionId}`);
};

export const getUserTopRoutes = async (userId, limit = 10) => {
  return await apiRequest('get', `/admin/users/${userId}/top-routes`, { 
    params: { limit } 
  });
};

// ============= GESTION DES AVENTURES (STORIES) =============

export const getAdminStories = async () => {
  return await apiRequest('get', '/admin/adventures/stories');
};

export const getAdminStory = async (storyId) => {
  return await apiRequest('get', `/admin/adventures/stories/${storyId}`);
};

export const createAdminStory = async (data) => {
  return await apiRequest('post', '/admin/adventures/stories', data);
};

export const updateAdminStory = async (storyId, data) => {
  return await apiRequest('put', `/admin/adventures/stories/${storyId}`, data);
};

export const publishAdminStory = async (storyId) => {
  return await apiRequest('post', `/admin/adventures/stories/${storyId}/publish`);
};

export const archiveAdminStory = async (storyId) => {
  return await apiRequest('post', `/admin/adventures/stories/${storyId}/archive`);
};

export const getAdminStoryAudit = async (storyId) => {
  return await apiRequest('get', `/admin/adventures/stories/${storyId}/audit`);
};

export const getAdminStoryAssets = async (storyId) => {
  return await apiRequest('get', `/admin/adventures/stories/${storyId}/assets`);
};

/** Télécharge un story pack (.dicto-story-pack.zip). */
export const exportAdminStoryPack = async (storyId) => {
  const response = await apiRequestStream('get', `/admin/adventures/stories/${storyId}/export-pack`);
  const blob = await response.blob();
  const disposition = response.headers.get('Content-Disposition') || '';
  const match = disposition.match(/filename="([^"]+)"/);
  const filename = match?.[1] || `${storyId}.dicto-story-pack.zip`;
  return { blob, filename };
};

/**
 * Importe un story pack en mettant à jour l'histoire en place (progression des enfants gardée).
 * mode : update | reject ; essai : compte rendu seul, rien d'écrit.
 */
export const importAdminStoryPack = async (file, { mode = 'update', essai = false } = {}) => {
  const formData = new FormData();
  formData.append('pack', file);
  return await apiRequest(
    'post',
    `/admin/adventures/stories/import-pack?mode=${mode}${essai ? '&essai=1' : ''}`,
    formData,
    {
      transformRequest: [(data, headers) => {
        delete headers['Content-Type'];
        return data;
      }],
    }
  );
};

export const uploadAdminStoryAsset = async (storyId, formData) => {
  return await apiRequest('post', `/admin/adventures/stories/${storyId}/assets/upload`, formData, {
    transformRequest: [(data, headers) => {
      delete headers['Content-Type'];
      return data;
    }],
  });
};

export const publishAdminStoryAsset = async (storyId, assetId) => {
  return await apiRequest('post', `/admin/adventures/stories/${storyId}/assets/${assetId}/publish`);
};

export const publishAllPendingAdminStoryAssets = async (storyId) => {
  return await apiRequest('post', `/admin/adventures/stories/${storyId}/assets/publish-pending`);
};

export const publishAdminStoryPresentation = async (storyId) => {
  return await apiRequest('post', `/admin/adventures/stories/${storyId}/presentation/publish`);
};

export const updateAdminStoryPresentation = async (storyId, data) => {
  return await apiRequest('put', `/admin/adventures/stories/${storyId}/presentation`, data);
};

/**
 * Collectibles proposables comme sceau de territoire achevé (migr. 111).
 * Un biome sans sceau affiche la coche des niveaux terminés — cas normal.
 */
export const getAdminSealCandidates = async () => {
  return await apiRequest('get', '/admin/adventures/seal-candidates');
};

export const createAdminBiome = async (data) => {
  return await apiRequest('post', '/admin/adventures/biomes', data);
};

export const updateAdminBiome = async (biomeId, data) => {
  return await apiRequest('put', `/admin/adventures/biomes/${biomeId}`, data);
};

export const deleteAdminBiome = async (biomeId) => {
  return await apiRequest('delete', `/admin/adventures/biomes/${biomeId}`);
};

export const updateAdminBiomePresentation = async (biomeId, data) => {
  return await apiRequest('put', `/admin/adventures/biomes/${biomeId}/presentation`, data);
};

export const publishAdminBiomePresentation = async (biomeId) => {
  return await apiRequest('post', `/admin/adventures/biomes/${biomeId}/presentation/publish`);
};

export const createAdminLevel = async (data) => {
  return await apiRequest('post', '/admin/adventures/levels', data);
};

export const updateAdminLevel = async (levelId, data) => {
  return await apiRequest('put', `/admin/adventures/levels/${levelId}`, data);
};

export const deleteAdminLevel = async (levelId) => {
  return await apiRequest('delete', `/admin/adventures/levels/${levelId}`);
};

// Le Grimoire et les pass : l'interrupteur de vente de chaque formule.
export const getProduitsPonctuels = async () => apiRequest('get', '/admin/products/ponctuels');
export const basculerProduitPonctuel = async (code, actif) => apiRequest('patch', `/admin/products/ponctuels/${encodeURIComponent(code)}`, { actif });
// Le catalogue des offres (migration 270) : prix, fenêtre de vente, présentation ; valeurs de choix (tomes, éditions).
export const modifierProduitPonctuel = async (code, champs) => apiRequest('patch', `/admin/products/ponctuels/${encodeURIComponent(code)}`, champs);
export const modifierOptionOffre = async (groupe, cle, champs) => apiRequest('patch', `/admin/products/options/${encodeURIComponent(groupe)}/${encodeURIComponent(cle)}`, champs);

// Le calendrier des offres du moment (migration 246) : ce que proposent les mails au parent.
export const getOffresDuMoment = async (params = {}) => apiRequest('get', '/admin/offres-du-moment', { params });
export const creerOffreDuMoment = async (offre) => apiRequest('post', '/admin/offres-du-moment', offre);
export const modifierOffreDuMoment = async (id, offre) => apiRequest('put', `/admin/offres-du-moment/${id}`, offre);
export const supprimerOffreDuMoment = async (id) => apiRequest('delete', `/admin/offres-du-moment/${id}`);
// Régler UNE semaine pour UN public : offreId null = « sans offre cette semaine ».
export const reglerSemaineOffre = async (lundi, publicCle, offreId) => apiRequest('put', `/admin/offres-du-moment/semaines/${lundi}/${publicCle}`, { offreId });
export const rendreSemaineAuCalendrier = async (lundi, publicCle) => apiRequest('delete', `/admin/offres-du-moment/semaines/${lundi}/${publicCle}`);

export const getProductsAudit = async () => {
  return await apiRequest('get', `/admin/products/audit`);
};

export const getParentFeedbacks = async (params = {}) => {
  return await apiRequest('get', `/admin/parent-feedbacks`, { params });
};

export const getParentFeedbackSynthesis = async () => {
  return await apiRequest('post', `/admin/parent-feedbacks/synthesis`);
};

export const updateAdminLevelPrerequisites = async (levelId, prerequisiteIds) => {
  return await apiRequest('put', `/admin/adventures/levels/${levelId}/prerequisites`, { prerequisiteIds });
};

// ── Modération : file de revue des signalements ─────────────────────────────
//
// 🔴 Une file que personne ne regarde est pire qu'une absence de file : elle
// donne l'illusion d'un traitement. Les stores exigent une réponse « en temps
// utile » à un signalement.

export const getContentReports = async (params = {}) => {
  return await apiRequest('get', `/moderation/reports`, { params });
};

/**
 * Statue sur un signalement.
 * ⚠️ Statuer n'efface AUCUN contenu : le retrait éventuel passe par les chemins
 * existants (archivage, exclusion), avec leurs propres garde-fous.
 */
export const reviewContentReport = async (id, status, note) => {
  return await apiRequest('patch', `/moderation/reports/${id}`, { status, note });
};

/** Rouvre la création de listes d'un enfant — lève tous ses refus de modération. */
export const liftModerationStrikes = async (uid) => {
  return await apiRequest('post', `/moderation/strikes/${encodeURIComponent(uid)}/lift`);
};


// ── Établissements clients (compte établissement, CE-1) ─────────────────────
export const getEtablissements = async () => apiRequest('get', '/admin/etablissements');

// ============= CONSOMMATION IA =============
/** `{ depuis, jusqua, total, parJour[], parModele[], parRaison[], parCapacite[], sansTarif[] }`, coûts par devise. */
export const getConsommationIA = async (jours = 30) => apiRequest('get', '/admin/consommation-ia', { params: { jours } });
export const getTarifsIA = async () => apiRequest('get', '/admin/consommation-ia/tarifs');
export const poserTarifIA = async (tarif) => apiRequest('post', '/admin/consommation-ia/tarifs', tarif);
export const getEntonnoirAchat = async (jours = 30) => apiRequest('get', '/admin/entonnoir-achat', { params: { jours } });
// ============= GRIMOIRES IMPRIMÉS (Cloudprinter) =============
/** `{ commandes[], compteurs: { statut: n } }` — sans adresse ; le détail la donne. */
export const getImpressions = async (statut = null) => apiRequest('get', '/admin/impressions', { params: statut ? { statut } : {} });
export const getImpression = async (id) => apiRequest('get', `/admin/impressions/${id}`);
export const relancerImpression = async (id) => apiRequest('post', `/admin/impressions/${id}/relancer`);
export const corrigerAdresseImpression = async (id, adresse) => apiRequest('put', `/admin/impressions/${id}/adresse`, { adresse });
export const annulerImpression = async (id, raison) => apiRequest('post', `/admin/impressions/${id}/annuler`, { raison });
export const refaireImpression = async (id, motif, adresse = null) => apiRequest('post', `/admin/impressions/${id}/refaire`, { motif, adresse });
export const actualiserImpression = async (id) => apiRequest('post', `/admin/impressions/${id}/actualiser`);
/** Le PDF de l'imprimeur d'une commande (celui envoyé, ou fabriqué avant l'envoi : jusqu'à une minute). */
export const telechargerFichierImpression = async (id) => {
  const response = await apiRequestStream('get', `/admin/impressions/${id}/fichier`);
  const blob = await response.blob();
  const match = (response.headers.get('Content-Disposition') || '').match(/filename="([^"]+)"/);
  return { blob, filename: match?.[1] || `grimoire-${id}.pdf` };
};
export const creerEtablissement = async (corps) => apiRequest('post', '/admin/etablissements', corps);
export const modifierEtablissement = async (id, corps) => apiRequest('patch', `/admin/etablissements/${id}`, corps);
export const finDeTestEtablissement = async (id, confirmation) =>
  apiRequest('post', `/admin/etablissements/${id}/fin-de-test`, { confirmation });
export const inviterGestionnaire = async (id, email) =>
  apiRequest('post', `/admin/etablissements/${id}/gestionnaires`, { email });
// Facturation (CE-8)
export const getFacturationEtablissement = async (id) => apiRequest('get', `/admin/etablissements/${id}/facturation`);
export const modifierConditionsCommerciales = async (id, corps) =>
  apiRequest('patch', `/admin/etablissements/${id}/facturation`, corps);
export const emettreFactureEtablissement = async (id, corps) =>
  apiRequest('post', `/admin/etablissements/${id}/factures`, corps);
export const actualiserFactureEtablissement = async (factureId) =>
  apiRequest('post', `/admin/etablissements/factures/${factureId}/actualiser`);

export const AdminService = {
  getOffresDuMoment,
  creerOffreDuMoment,
  modifierOffreDuMoment,
  supprimerOffreDuMoment,
  reglerSemaineOffre,
  rendreSemaineAuCalendrier,
  getConsommationIA,
  getEntonnoirAchat,
  getImpressions,
  getImpression,
  relancerImpression,
  corrigerAdresseImpression,
  annulerImpression,
  refaireImpression,
  actualiserImpression,
  telechargerFichierImpression,
  getTarifsIA,
  poserTarifIA,
  getEtablissements,
  creerEtablissement,
  modifierEtablissement,
  inviterGestionnaire,
  finDeTestEtablissement,
  getFacturationEtablissement,
  modifierConditionsCommerciales,
  emettreFactureEtablissement,
  actualiserFactureEtablissement,
  getContentReports,
  reviewContentReport,
  liftModerationStrikes,
  // Statistiques
  getDashboardStats,
  getOnboardingFunnel,
  getAccountMilestones,
  getAcquisitionBySource,
  getActivePlayers,
  getNewFamilies,
  getPaidCustomers,
  getPlaySessions,
  getAdventureMilestones,
  getAdventureLevelDaily,
  getServingGaps,
  getNotionsDemandees,
  marquerNotionDemandeeAjoutee,
  getTeacherPages,
  probeErrorClassification,
  listErrorClassificationCache,
  getErrorClassificationHeuristic,
  classifyErrorHeuristic,

  // Exercices
  getExercises,
  getExerciseSources,
  getExerciseTaxonomyStats,
  reassignExerciseTaxonomy,
  getExerciseById,
  createExercise,
  updateExercise,
  deleteExercise,
  toggleExerciseStatus,
  generateExercise,
  getExerciseUsageStats,
  getExerciseNeighbours,
  
  // Prompts
  getPrompts,
  getActivePromptsByGameType,
  getPromptById,
  createPrompt,
  updatePrompt,
  deletePrompt,
  logPromptUsage,
  getFormatInstructions,
  
  // ✨ Preview et Test de Prompts
  evaluateExercise,
  adaptExercise,
  optimizePrompt,
  previewPrompt,
  testPrompt,
  savePromptVersion,
  comparePrompts,
  getPromptHistory,
  analyzePromptQuality,
  
  // ✨ Chat avec LLM
  chatWithLLM,
  getSuggestedPrompts,
  correctExerciseWithLLM,
  correctExerciseItemWithLLM,

  // ✨ Correction en masse
  startBulkCorrection,
  applyBulkCorrections,
  finalizeBulkSafeguard,
  validateBulkExercises,
  
  // Utilisateurs
  getUsers,
  getUsersEvolution,
  getUserById,
  toggleUserStatus,
  toggleUserAdminStatus,
  setUserAdminRole,
  getAdminSession,
  getUserCombatStats,
  getUserGameStats,       // alias rétrocompatible vers getUserCombatStats
  getUserChildren,
  getUserParent,
  getUserCompetences,
  getUserLevelEstimate,
  getUserUsageCredits,
  grantUserUsageCredit,
  grantUserSubscription,
  getUserSubscriptionGrants,
  
  // Navigation
  getUserNavigationHistory,
  getUserSessions,
  getUserSessionDetails,
  getUserTopRoutes,

  // Analytics marketing
  getMarketingAnalytics,

  // Aventures (stories)
  getAdminStories,
  getAdminStory,
  createAdminStory,
  updateAdminStory,
  publishAdminStory,
  archiveAdminStory,
  getAdminStoryAudit,
  getAdminStoryAssets,
  exportAdminStoryPack,
  importAdminStoryPack,
  updateAdminStoryPresentation,
  publishAdminStoryPresentation,
  createAdminBiome,
  updateAdminBiome,
  deleteAdminBiome,
  updateAdminBiomePresentation,
  publishAdminBiomePresentation,
  createAdminLevel,
  updateAdminLevel,
  deleteAdminLevel,
  updateAdminLevelPrerequisites,

  // Audit produits / monétisation
  getProductsAudit,
  getProduitsPonctuels,
  basculerProduitPonctuel,
  modifierProduitPonctuel,
  modifierOptionOffre,

  // Catalogue items
  getCatalogItems,
  getCatalogItem,
  updateCatalogItem,
  createCatalogItem,
  getCatalogIconFrames,
  uploadCatalogItemDrawing,
  simulateCatalogEffects,

  // Feedbacks parents
  getParentFeedbacks,
  getParentFeedbackSynthesis,
};
// Catalogue partagé des habillages de scénarios.
export const getAdminScenarios = () => apiRequest('get', '/admin/scenarios');
export const createScenarioVariant = (data) => apiRequest('post', '/admin/scenarios/variants', data);
export const updateScenarioVariant = (id, data) => apiRequest('put', `/admin/scenarios/variants/${id}`, data);
export const publishScenarioVariant = (id, revision) => apiRequest('post', `/admin/scenarios/variants/${id}/publish`, { revision });
export const uploadScenarioImage = (file) => {
  const form = new FormData(); form.append('file', file);
  return apiRequest('post', '/admin/scenarios/assets', form, { headers: { 'Content-Type': 'multipart/form-data' } });
};
// Livraison et historique des variantes (backend/config/scenarios/README.md § Livraison).
export const getScenarioVariantHistory = (id) => apiRequest('get', `/admin/scenarios/variants/${id}/historique`);
export const restoreScenarioVariant = (id, publicationId, revision) =>
  apiRequest('post', `/admin/scenarios/variants/${id}/restaurer`, { publicationId, revision });
export const exportScenarioVariant = (id) => apiRequest('post', `/admin/scenarios/variants/${id}/exporter`);
export const getScenarioVariantPackage = (id) => apiRequest('get', `/admin/scenarios/variants/${id}/paquet`);
